import { Component, OnDestroy, OnInit, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { dsDtos } from '@vectronic/openApi-admin';
import { utcMoment } from '@vectronic/util';
import { Subscription, filter } from 'rxjs';
import { CameraScheduleService } from '../../../services/camera-schedule.service';

@Component({
  selector: 'rc-camera-calendar-year-view',
  templateUrl: './camera-calendar-year-view.component.html',
  styleUrls: ['./camera-calendar-year-view.component.scss'],
  imports: [MatIconButton, MatIcon, NgTemplateOutlet, NgClass, MatTooltip, DatePipe],
})
export class CameraCalendarYearViewComponent implements OnInit, OnDestroy {
  cameraScheduleService = inject(CameraScheduleService);

  selectedDate = input<Date | null>();

  monthChange = output<Date>();
  yearChange = output<Date>();

  rulesArray = signal<dsDtos.CameraScheduleRule[]>(undefined);

  subscription: Subscription;

  months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  fixesOfLastYearMonths: { number: number; videoLength: number; photoCount: number }[];
  fixesOfNextYearMonths: { number: number; videoLength: number; photoCount: number }[];
  fixesOfMonths: { number: number; videoLength: number; photoCount: number }[];
  averageOfMonths: number[];
  averageOfNextYearMonths: number[];
  averageOfLastYearMonths: number[];

  constructor() {
    toObservable(this.selectedDate)
      .pipe(
        takeUntilDestroyed(),
        filter((v) => v != null),
      )
      .subscribe(() => {
        this.fixesOfMonths = this.months.map((value, index) =>
          this.numberOfSchedulesInMonth(index),
        );
        this.fixesOfLastYearMonths = this.months.map((value, index) =>
          this.numberOfSchedulesInMonth(index, -1),
        );
        this.fixesOfNextYearMonths = this.months.map((value, index) =>
          this.numberOfSchedulesInMonth(index, 1),
        );
        this.averageOfMonths = this.months.map((value, index) => {
          const numberOfDaysInMonth = utcMoment(this.selectedDate(), true)
            .set('month', index)
            .daysInMonth();
          return this.fixesOfMonths[index].number / numberOfDaysInMonth || 0;
        });
        this.averageOfLastYearMonths = this.months.map((value, index) => {
          const numberOfDaysInMonth = utcMoment(this.selectedDate(), true)
            .set('month', index)
            .daysInMonth();
          return this.fixesOfLastYearMonths[index].number / numberOfDaysInMonth || 0;
        });
        this.averageOfNextYearMonths = this.months.map((value, index) => {
          const numberOfDaysInMonth = utcMoment(this.selectedDate(), true)
            .set('month', index)
            .daysInMonth();
          return this.fixesOfNextYearMonths[index].number / numberOfDaysInMonth || 0;
        });
      });
  }

  ngOnInit() {
    this.subscription = this.cameraScheduleService.rules$.subscribe((rules) => {
      this.rulesArray.set(Object.values(rules));
      this.fixesOfMonths = this.months.map((value, index) => this.numberOfSchedulesInMonth(index));
      this.fixesOfLastYearMonths = this.months.map((value, index) =>
        this.numberOfSchedulesInMonth(index, -1),
      );
      this.fixesOfNextYearMonths = this.months.map((value, index) =>
        this.numberOfSchedulesInMonth(index, 1),
      );
      this.averageOfMonths = this.months.map((value, index) => {
        const numberOfDaysInMonth = utcMoment(this.selectedDate(), true)
          .set('month', index)
          .daysInMonth();
        return this.fixesOfMonths[index].number / numberOfDaysInMonth || 0;
      });
      this.averageOfLastYearMonths = this.months.map((value, index) => {
        const numberOfDaysInMonth = utcMoment(this.selectedDate(), true)
          .set('month', index)
          .daysInMonth();
        return this.fixesOfLastYearMonths[index].number / numberOfDaysInMonth || 0;
      });
      this.averageOfNextYearMonths = this.months.map((value, index) => {
        const numberOfDaysInMonth = utcMoment(this.selectedDate(), true)
          .set('month', index)
          .daysInMonth();
        return this.fixesOfNextYearMonths[index].number / numberOfDaysInMonth || 0;
      });
    });
  }

  get maxFix() {
    return Math.max(
      ...this.averageOfMonths,
      ...this.averageOfLastYearMonths,
      ...this.averageOfNextYearMonths,
    );
  }

  numberOfSchedulesInMonth(
    month: number,
    addYear = 0,
  ): { number: number; videoLength: number; photoCount: number } {
    let number = 0;
    let videoLength = 0;
    let photoCount = 0;
    const date = utcMoment(this.selectedDate(), true).add({ year: addYear }).set('month', month);
    this.rulesArray()?.forEach((rule) => {
      let count: number;
      if (this.cameraScheduleService.isNonTimerTrigger(rule)) {
        count = this.cameraScheduleService.numberOfTimespansInMonth(rule, date);
      } else {
        count = this.cameraScheduleService?.numberOfSchedulesInMonth(rule, date);
      }
      number += count;
      if (rule.cameraScheduleRuleType === 'VIDEO') {
        videoLength += rule.size * count;
      } else {
        photoCount += rule.size * count;
      }
    });
    return {
      number,
      videoLength,
      photoCount,
    };
  }

  onClickItem(index: number, isClickable: boolean) {
    if (isClickable)
      this.monthChange.emit(utcMoment(this.selectedDate(), true).set('month', index).toDate());
  }

  onClickPrevious() {
    this.yearChange.emit(utcMoment(this.selectedDate(), true).add('year', -1).toDate());
  }

  onClickNext() {
    this.yearChange.emit(utcMoment(this.selectedDate(), true).add('year', 1).toDate());
  }

  getPercent(average: number) {
    return Math.round((average * 100) / this.maxFix) || 0;
  }
  getDurationString(value: number): string {
    if (value === 0) return '0s';
    const seconds = Number(value);
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const dDisplay = d > 0 ? d + 'd ' : '';
    const hDisplay = h > 0 ? h + 'h ' : '';
    const mDisplay = m > 0 ? m + 'm ' : '';
    const sDisplay = s > 0 ? s + 's ' : '';
    return (dDisplay + hDisplay + mDisplay + sDisplay).trim();
  }

  getTooltip(fixRate: { number: number; videoLength: number; photoCount: number }): string {
    if (!fixRate.number) return 'No fixes';
    let string = `${fixRate.number} fixes`;
    if (fixRate.videoLength) string += ` (Videos: ${this.getDurationString(fixRate.videoLength)})`;
    if (fixRate.photoCount) string += ` (Photos: ${fixRate.photoCount})`;
    return string;
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
